/**
 * BinanceArb.tsx — Binance CEX Funding Arbitrage
 * Spot + Perpetual Futures USDC pairs
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Settings, ChevronDown, ChevronUp, Trash2, XCircle,
  Zap, Star, Search, AlertTriangle, CheckCircle,
} from 'lucide-react';
import { getTheme } from '../App';
import { useToast } from '../components/Toast';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BinanceTicker {
  base:               string;
  quote:              string;   // 'USDC' | 'USDT'
  spot_sym:           string;
  fut_sym:            string;
  spot_bid:           number;
  spot_ask:           number;
  spot_mid:           number;
  fut_bid:            number;
  fut_ask:            number;
  fut_mid:            number;
  mark_price:         number;
  index_price:        number;
  last_funding:       number;   // son gerçekleşen (lastFundingRate)
  predicted_funding:  number;   // tahmini sonraki (mark/index spread)
  est_funding:        number;   // = predicted_funding (sort/threshold için)
  next_funding_ms:    number;
  next_funding_in:    number;
  basis_pct:          number;
  volume_24h:         number;
  change_24h:         number;
  spot_maker_fee:     number;
  spot_taker_fee:     number;
  fut_maker_fee:      number;
  fut_taker_fee:      number;
}

interface BinanceAccount {
  spot_usdc?:           number;
  spot_usdt?:           number;
  fut_usdc_balance?:    number;
  fut_usdc_available?:  number;
  fut_usdt_balance?:    number;
  fut_usdt_available?:  number;
  fut_unrealized?:      number;
  positions?:           FutPosition[];
}

interface FutPosition {
  symbol:          string;
  side:            'long' | 'short';
  quantity:        number;
  entry_price:     number;
  mark_price:      number;
  unrealized_pnl:  number;
  leverage:        number;
  notional:        number;
  liq_price:       number | null;
  margin_type:     string;
}

interface ArbPos {
  id:               number;
  base:             string;
  quote:            string;
  spot_sym:         string;
  fut_sym:          string;
  direction:        'long_spot_short_fut' | 'short_spot_long_fut';
  status:           'opening' | 'open' | 'partial_close' | 'closed' | 'aborted';
  entry_funding:    number;
  spot_entry:       number;
  fut_entry:        number;
  spot_qty:         number;
  fut_qty:          number;
  realized_pnl:     number;
  funding_collected: number;
  created_at:       number;
  log:              { ts: number; msg: string }[];
}

interface BinanceConfig {
  active_pairs:        string[];
  funding_threshold:   number;
  position_size_usdc:  number;
  leverage:            number;
  max_open_positions:  number;
  min_volume_24h:      number;
}

type PriceType = 'bid' | 'ask' | 'mid';
type Direction = 'long_spot_short_fut' | 'short_spot_long_fut';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtPrice(p: number | undefined | null): string {
  if (p == null || isNaN(p as number) || !p) return '—';
  if (p >= 10000) return p.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (p >= 100)   return p.toFixed(2);
  if (p >= 1)     return p.toFixed(4);
  return p.toFixed(6);
}
function fmtPct(v: number | undefined | null, digits = 4): string {
  if (v == null || isNaN(v as number)) return '—%';
  return ((v as number) * 100).toFixed(digits) + '%';
}
function fmtVol(v: number | undefined | null): string {
  const n = v == null || isNaN(v as number) ? 0 : (v as number);
  if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toFixed(0);
}
function fmtCountdown(ms: number): string {
  if (ms <= 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}s ${m}d`;
  return `${m}d ${sec.toString().padStart(2, '0')}s`;
}
function fmtTime(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString('tr-TR');
}

const FAVS_KEY = 'binance_arb_favs';
function loadFavs(): string[] {
  try { return JSON.parse(localStorage.getItem(FAVS_KEY) || '[]'); }
  catch { return []; }
}

function pickPrice(tk: BinanceTicker, leg: 'spot' | 'fut', ptype: PriceType): number {
  if (leg === 'spot') return ptype === 'bid' ? tk.spot_bid : ptype === 'ask' ? tk.spot_ask : tk.spot_mid;
  return ptype === 'bid' ? tk.fut_bid : ptype === 'ask' ? tk.fut_ask : tk.fut_mid;
}

// ─── Entry Modal ──────────────────────────────────────────────────────────────

function EntryModal({ ticker, cfg, dark, onConfirm, onClose }: {
  ticker: BinanceTicker;
  cfg: BinanceConfig;
  dark: boolean;
  onConfirm: (payload: object) => void;
  onClose: () => void;
}) {
  const t = getTheme(dark);
  const suggestedDir: Direction = ticker.est_funding >= 0 ? 'long_spot_short_fut' : 'short_spot_long_fut';
  const [direction,      setDirection]      = useState<Direction>(suggestedDir);
  const [spotPriceType,  setSpotPriceType]  = useState<PriceType>(direction === 'long_spot_short_fut' ? 'ask' : 'bid');
  const [futPriceType,   setFutPriceType]   = useState<PriceType>(direction === 'long_spot_short_fut' ? 'bid' : 'ask');

  const spotPrice = pickPrice(ticker, 'spot', spotPriceType);
  const futPrice  = pickPrice(ticker, 'fut',  futPriceType);
  const refPrice  = ticker.spot_mid || ticker.fut_mid;
  const qty       = refPrice > 0 ? cfg.position_size_usdc / refPrice : 0;
  const notional  = qty * spotPrice;

  const [openSpot, setOpenSpot] = useState(true);
  const [openFut,  setOpenFut]  = useState(true);

  const meetsThreshold = Math.abs(ticker.est_funding) >= cfg.funding_threshold;

  // When direction changes, suggest sensible price types
  const handleDirection = (d: Direction) => {
    setDirection(d);
    if (d === 'long_spot_short_fut') { setSpotPriceType('ask'); setFutPriceType('bid'); }
    else                              { setSpotPriceType('bid'); setFutPriceType('ask'); }
  };

  const LegToggle = ({ label, checked, onChange, side }: {
    label: string; checked: boolean; onChange: (v: boolean) => void; side: 'spot' | 'fut';
  }) => (
    <button
      onClick={() => onChange(!checked)}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${
        checked
          ? side === 'spot'
            ? 'bg-emerald-500/15 border-emerald-500 text-emerald-400'
            : 'bg-blue-500/15 border-blue-500 text-blue-400'
          : dark
            ? 'bg-[#0a2560]/20 border-[#003087]/40 text-blue-600'
            : 'bg-blue-50 border-blue-200 text-blue-300'
      }`}
    >
      <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border text-[10px] font-bold ${
        checked
          ? side === 'spot' ? 'bg-emerald-500 border-emerald-500 text-black' : 'bg-blue-500 border-blue-500 text-white'
          : 'border-current'
      }`}>{checked ? '✓' : ''}</span>
      {label}
    </button>
  );

  const priceBtn = (label: string, active: boolean, onClick: () => void) => (
    <button onClick={onClick}
      className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${
        active
          ? 'bg-[#F0B90B] text-black border-[#F0B90B]'
          : dark ? 'bg-[#0a2560]/40 text-blue-300 border-[#003087] hover:border-[#4A9FE8]'
                 : 'bg-white text-blue-700 border-blue-300 hover:border-blue-500'
      }`}
    >{label}</button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`${t.card} border ${t.cardBorder} rounded-2xl w-full max-w-md`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${t.cardBorder}`}>
          <div>
            <h2 className="font-bold text-base text-[#F0B90B]">{ticker.base}/{ticker.quote} — Pozisyon Aç</h2>
            <p className={`text-xs mt-0.5 ${dark ? 'text-blue-400' : 'text-blue-600'}`}>
              Tahmini: <span className={(ticker.predicted_funding ?? 0) >= 0 ? t.positive : t.negative}>
                {(ticker.predicted_funding ?? 0) >= 0 ? '+' : ''}{fmtPct(ticker.predicted_funding ?? 0)}
              </span>
              <span className="mx-1 opacity-40">·</span>
              Son: <span className={`opacity-60 ${(ticker.last_funding ?? 0) >= 0 ? t.positive : t.negative}`}>
                {(ticker.last_funding ?? 0) >= 0 ? '+' : ''}{fmtPct(ticker.last_funding ?? 0)}
              </span>
              {!meetsThreshold && <span className="ml-2 text-amber-400">⚠ Eşik altı</span>}
            </p>
          </div>
          <button onClick={onClose} className={`${dark ? 'text-blue-400 hover:text-white' : 'text-blue-600'}`}>✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* Direction */}
          <div>
            <label className={`block text-xs font-semibold mb-2 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>Yön</label>
            <div className="grid grid-cols-2 gap-2">
              {(['long_spot_short_fut', 'short_spot_long_fut'] as Direction[]).map(d => (
                <button key={d} onClick={() => handleDirection(d)}
                  className={`py-2 rounded-xl text-xs font-semibold border transition-colors ${
                    direction === d
                      ? 'bg-[#F0B90B]/20 border-[#F0B90B] text-[#F0B90B]'
                      : dark ? 'bg-[#0a2560]/30 border-[#003087] text-blue-300 hover:border-[#4A9FE8]'
                             : 'bg-white border-blue-300 text-blue-600 hover:border-blue-500'
                  }`}>
                  {d === 'long_spot_short_fut' ? '↑ Spot Long / Fut Short' : '↓ Spot Short / Fut Long'}
                  <span className={`ml-1 text-[10px] ${d === suggestedDir ? t.positive : ''}`}>
                    {d === suggestedDir ? '(önerilen)' : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Leg Selection */}
          <div>
            <label className={`block text-xs font-semibold mb-2 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>Leg Seçimi</label>
            <div className="flex items-center gap-2">
              <LegToggle label="Spot" checked={openSpot} onChange={setOpenSpot} side="spot" />
              <LegToggle label="Futures" checked={openFut} onChange={setOpenFut} side="fut" />
            </div>
            {!openSpot && !openFut && (
              <p className="text-xs text-rose-400 mt-1.5">⚠ En az bir leg seçili olmalı</p>
            )}
            {openSpot && !openFut && (
              <p className={`text-xs mt-1.5 ${dark ? 'text-blue-500' : 'text-blue-400'}`}>Yalnızca spot emir gönderilecek — hedge yok</p>
            )}
            {!openSpot && openFut && (
              <p className={`text-xs mt-1.5 ${dark ? 'text-blue-500' : 'text-blue-400'}`}>Yalnızca futures emir gönderilecek — hedge yok</p>
            )}
          </div>

          {/* Spot price */}
          {openSpot && (
          <div>
            <label className={`block text-xs font-semibold mb-2 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
              Spot Fiyat Türü
              <span className={`ml-2 font-mono ${dark ? 'text-white' : 'text-blue-900'}`}>
                → {fmtPrice(spotPrice)}
              </span>
            </label>
            <div className="flex items-center gap-2">
              {(['bid', 'ask', 'mid'] as PriceType[]).map(pt => (
                priceBtn(
                  pt === 'bid' ? `Bid ${fmtPrice(ticker.spot_bid)}` :
                  pt === 'ask' ? `Ask ${fmtPrice(ticker.spot_ask)}` :
                  `Mid ${fmtPrice(ticker.spot_mid)}`,
                  spotPriceType === pt,
                  () => setSpotPriceType(pt),
                )
              ))}
            </div>
          </div>
          )}

          {/* Futures price */}
          {openFut && (
          <div>
            <label className={`block text-xs font-semibold mb-2 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
              Futures Fiyat Türü
              <span className={`ml-2 font-mono ${dark ? 'text-white' : 'text-blue-900'}`}>
                → {fmtPrice(futPrice)}
              </span>
            </label>
            <div className="flex items-center gap-2">
              {(['bid', 'ask', 'mid'] as PriceType[]).map(pt => (
                priceBtn(
                  pt === 'bid' ? `Bid ${fmtPrice(ticker.fut_bid)}` :
                  pt === 'ask' ? `Ask ${fmtPrice(ticker.fut_ask)}` :
                  `Mid ${fmtPrice(ticker.fut_mid)}`,
                  futPriceType === pt,
                  () => setFutPriceType(pt),
                )
              ))}
            </div>
          </div>
          )}

          {/* Summary */}
          <div className={`rounded-xl p-3 text-xs space-y-1.5 ${dark ? 'bg-[#030d1f]' : 'bg-blue-50'}`}>
            <div className="flex justify-between">
              <span className={dark ? 'text-blue-500' : 'text-blue-400'}>Miktar</span>
              <span className="font-mono">{qty.toFixed(4)} {ticker.base}</span>
            </div>
            <div className="flex justify-between">
              <span className={dark ? 'text-blue-500' : 'text-blue-400'}>Notional</span>
              <span className="font-mono">≈${notional.toFixed(2)}</span>
            </div>
            {openSpot && (
            <div className="flex justify-between">
              <span className={dark ? 'text-blue-500' : 'text-blue-400'}>Spot Emir</span>
              <span className={`font-mono ${direction === 'long_spot_short_fut' ? t.positive : t.negative}`}>
                {direction === 'long_spot_short_fut' ? 'BUY' : 'SELL'} @ {fmtPrice(spotPrice)}
              </span>
            </div>
            )}
            {openFut && (
            <div className="flex justify-between">
              <span className={dark ? 'text-blue-500' : 'text-blue-400'}>Futures Emir</span>
              <span className={`font-mono ${direction === 'short_spot_long_fut' ? t.positive : t.negative}`}>
                {direction === 'long_spot_short_fut' ? 'SELL' : 'BUY'} @ {fmtPrice(futPrice)}
              </span>
            </div>
            )}
            {openFut && (
            <div className="flex justify-between">
              <span className={dark ? 'text-blue-500' : 'text-blue-400'}>Kaldıraç</span>
              <span className="font-mono">{cfg.leverage}x isolated</span>
            </div>
            )}
            <div className="flex justify-between">
              <span className={dark ? 'text-blue-500' : 'text-blue-400'}>Sonraki Funding</span>
              <span className="font-mono">{fmtCountdown(ticker.next_funding_in)}</span>
            </div>
          </div>

          {!meetsThreshold && (
            <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-400/10 rounded-lg px-3 py-2">
              <AlertTriangle size={12} />
              Funding eşiğin ({fmtPct(cfg.funding_threshold)}) altında — yine de açabilirsin
            </div>
          )}
        </div>

        <div className={`flex gap-2 px-5 py-4 border-t ${t.cardBorder}`}>
          <button
            disabled={!openSpot && !openFut}
            onClick={() => onConfirm({ spot_sym: ticker.spot_sym, direction, spot_price_type: spotPriceType, fut_price_type: futPriceType, open_spot: openSpot, open_futures: openFut })}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-[#F0B90B] hover:bg-[#d4a209] text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Pozisyon Aç
          </button>
          <button onClick={onClose}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold ${t.btnSecondary} transition-colors`}>
            İptal
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ArbCard ──────────────────────────────────────────────────────────────────

function ArbCard({ arb, ticker, dark, onClose, onAbort, onDelete, onPartialClose }: {
  arb: ArbPos; ticker?: BinanceTicker; dark: boolean;
  onClose: (id: number, pct: number) => void;
  onAbort: (id: number) => void;
  onDelete: (id: number) => void;
  onPartialClose: (id: number, pct: number) => void;
}) {
  const t = getTheme(dark);
  const [expanded,      setExpanded]      = useState(false);
  const [closePct,      setClosePct]      = useState('50');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const dirLabel = arb.direction === 'long_spot_short_fut' ? '↑ Spot Long / Fut Short' : '↓ Spot Short / Fut Long';
  const statusColor: Record<string, string> = {
    opening: 'text-amber-400', open: 'text-emerald-400',
    partial_close: 'text-blue-400', closed: 'text-blue-400', aborted: 'text-rose-400',
  };
  const notional = arb.spot_qty * (arb.spot_entry || ticker?.spot_mid || 0);

  return (
    <div className={`${t.card} border ${t.cardBorder} rounded-xl overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="text-sm font-bold">{arb.base}<span className={`text-xs ml-0.5 ${arb.quote === 'USDT' ? 'text-emerald-500' : 'text-amber-500'}`}>/{arb.quote ?? 'USDC'}</span></div>
          <span className={`text-xs font-mono ${statusColor[arb.status] || 'text-gray-400'}`}>{arb.status.toUpperCase()}</span>
          <span className={`text-xs ${dark ? 'text-blue-400' : 'text-blue-600'}`}>{dirLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${dark ? 'text-blue-500' : 'text-blue-400'}`}>{fmtTime(arb.created_at)}</span>
          {(arb.status === 'opening' || arb.status === 'partial_close') && (
            <button onClick={() => onAbort(arb.id)}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-orange-500/20 text-orange-400 hover:bg-orange-500/40 transition-colors">
              <XCircle size={12} /> Durdur
            </button>
          )}
          {deleteConfirm ? (
            <div className="flex items-center gap-1">
              <button onClick={() => onDelete(arb.id)} className="px-2 py-1 rounded text-xs bg-rose-600 text-white hover:bg-rose-700">Sil</button>
              <button onClick={() => setDeleteConfirm(false)} className={`px-2 py-1 rounded text-xs ${t.btnSecondary}`}>İptal</button>
            </div>
          ) : (
            <button onClick={() => setDeleteConfirm(true)}
              className={`p-1.5 rounded ${dark ? 'text-blue-500 hover:text-rose-400 hover:bg-[#0a2560]' : 'text-blue-400 hover:text-rose-600 hover:bg-blue-100'} transition-colors`}>
              <Trash2 size={13} />
            </button>
          )}
          <button onClick={() => setExpanded(!expanded)} className={`p-1 ${dark ? 'text-blue-400' : 'text-blue-600'}`}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      <div className={`px-4 py-2 grid grid-cols-4 gap-3 border-t ${t.cardBorder} text-xs`}>
        <div><div className={`${dark ? 'text-blue-500' : 'text-blue-400'} mb-0.5`}>Giriş Funding</div>
          <div className={arb.entry_funding >= 0 ? t.positive : t.negative}>{fmtPct(arb.entry_funding)}</div></div>
        <div><div className={`${dark ? 'text-blue-500' : 'text-blue-400'} mb-0.5`}>Spot Giriş</div>
          <div className="font-mono">{fmtPrice(arb.spot_entry) || '—'}</div></div>
        <div><div className={`${dark ? 'text-blue-500' : 'text-blue-400'} mb-0.5`}>Fut Giriş</div>
          <div className="font-mono">{fmtPrice(arb.fut_entry) || '—'}</div></div>
        <div><div className={`${dark ? 'text-blue-500' : 'text-blue-400'} mb-0.5`}>Miktar</div>
          <div>{(arb.spot_qty ?? 0).toFixed(4)} <span className={`${dark ? 'text-blue-500' : 'text-blue-400'}`}>≈${notional.toFixed(1)}</span></div></div>
      </div>

      {expanded && (
        <div className={`border-t ${t.cardBorder} px-4 py-3 space-y-3`}>
          {(arb.status === 'open' || arb.status === 'partial_close') && (
            <div>
              <div className={`text-xs font-semibold mb-2 text-[#F0B90B]`}>Kademeli Kapatma</div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input type="number" min={1} max={100} step={1} value={closePct}
                    onChange={e => setClosePct(e.target.value)}
                    className={`w-full text-xs px-3 py-1.5 rounded-lg border outline-none ${t.inp}`} />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${dark ? 'text-blue-500' : 'text-blue-400'}`}>%</span>
                </div>
                <button onClick={() => onPartialClose(arb.id, parseFloat(closePct) / 100)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${t.btnPrimary} transition-colors`}>Kapat</button>
                <button onClick={() => onClose(arb.id, 1.0)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white transition-colors">Tamamını Kapat</button>
              </div>
            </div>
          )}
          {ticker && (
            <div className={`grid grid-cols-4 gap-2 text-xs ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
              <div><span className={`${dark ? 'text-blue-500' : 'text-blue-400'} block`}>Spot Mid</span>{fmtPrice(ticker.spot_mid)}</div>
              <div><span className={`${dark ? 'text-blue-500' : 'text-blue-400'} block`}>Fut Mid</span>{fmtPrice(ticker.fut_mid)}</div>
              <div><span className={`${dark ? 'text-blue-500' : 'text-blue-400'} block`}>Funding</span>
                <span className={ticker.est_funding >= 0 ? t.positive : t.negative}>{fmtPct(ticker.est_funding)}</span></div>
              <div><span className={`${dark ? 'text-blue-500' : 'text-blue-400'} block`}>Sonraki</span>{fmtCountdown(ticker.next_funding_in)}</div>
            </div>
          )}
          {arb.log.length > 0 && (
            <div className={`text-xs font-mono ${dark ? 'text-blue-500' : 'text-blue-400'} space-y-0.5`}>
              {arb.log.slice(-5).map((l, i) => <div key={i}>[{fmtTime(l.ts)}] {l.msg}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Config Modal ─────────────────────────────────────────────────────────────

function ConfigModal({ cfg, tickers, dark, onSave, onClose }: {
  cfg: BinanceConfig; tickers: BinanceTicker[]; dark: boolean;
  onSave: (c: BinanceConfig) => void; onClose: () => void;
}) {
  const t = getTheme(dark);
  const [local, setLocal] = useState<BinanceConfig>({ ...cfg });
  const set = (k: keyof BinanceConfig, v: any) => setLocal(prev => ({ ...prev, [k]: v }));
  const topTicker = tickers[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className={`${t.card} border ${t.cardBorder} rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
        <div className={`flex items-center justify-between px-6 py-4 border-b ${t.cardBorder}`}>
          <h2 className="font-bold text-base text-[#F0B90B]">Binance Arb Ayarları</h2>
          <button onClick={onClose} className={`${dark ? 'text-blue-400 hover:text-white' : 'text-blue-600'}`}>✕</button>
        </div>
        <div className="px-6 py-4 space-y-5">

          {/* Funding threshold — sadece ⚡ göstergesi için, butonu disable etmez */}
          <div>
            <label className={`block text-xs font-semibold mb-1.5 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
              Funding Eşiği <span className={`font-normal ${dark ? 'text-blue-500' : 'text-blue-400'}`}>(⚡ göstergesi için)</span>
            </label>
            <div className="relative">
              <input type="number" step="0.0001" min={0} value={local.funding_threshold}
                onChange={e => set('funding_threshold', parseFloat(e.target.value) || 0)}
                className={`w-full text-xs px-3 py-2 rounded-lg border outline-none ${t.inp}`} />
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${dark ? 'text-blue-500' : 'text-blue-400'}`}>
                = {fmtPct(local.funding_threshold)}
              </span>
            </div>
            {topTicker && (
              <div className={`mt-1 text-xs flex items-center gap-1 ${Math.abs(topTicker.est_funding) >= local.funding_threshold ? t.positive : 'text-amber-400'}`}>
                {Math.abs(topTicker.est_funding) >= local.funding_threshold ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
                En yüksek: {topTicker.base} {fmtPct(topTicker.est_funding)} funding
              </div>
            )}
          </div>

          {/* Position size */}
          <div>
            <label className={`block text-xs font-semibold mb-1.5 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
              Pozisyon Büyüklüğü (her leg, USDC)
            </label>
            <div className="relative">
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs ${dark ? 'text-blue-500' : 'text-blue-400'}`}>$</span>
              <input type="number" step="10" min={10} value={local.position_size_usdc}
                onChange={e => set('position_size_usdc', parseFloat(e.target.value) || 100)}
                className={`w-full text-xs pl-6 pr-3 py-2 rounded-lg border outline-none ${t.inp}`} />
            </div>
          </div>

          {/* Leverage */}
          <div>
            <label className={`block text-xs font-semibold mb-1.5 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>Futures Kaldıraç</label>
            <input type="number" step="1" min={1} max={125} value={local.leverage}
              onChange={e => set('leverage', parseInt(e.target.value) || 10)}
              className={`w-full text-xs px-3 py-2 rounded-lg border outline-none ${t.inp}`} />
            <p className={`mt-1 text-xs ${dark ? 'text-blue-600' : 'text-blue-400'}`}>Isolated margin — futures leg'e uygulanır</p>
          </div>

          {/* Max open positions */}
          <div>
            <label className={`block text-xs font-semibold mb-1.5 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
              Maks. Açık Pozisyon <span className={`font-normal ${dark ? 'text-blue-500' : 'text-blue-400'}`}>(0 = sınırsız)</span>
            </label>
            <input type="number" step="1" min={0} value={local.max_open_positions}
              onChange={e => set('max_open_positions', parseInt(e.target.value) || 0)}
              className={`w-full text-xs px-3 py-2 rounded-lg border outline-none ${t.inp}`} />
          </div>

          {/* Min volume */}
          <div>
            <label className={`block text-xs font-semibold mb-1.5 ${dark ? 'text-blue-300' : 'text-blue-700'}`}>
              Min. 24s Hacim Filtresi ($) <span className={`font-normal ${dark ? 'text-blue-500' : 'text-blue-400'}`}>(0 = tümü)</span>
            </label>
            <input type="number" step="100000" min={0} value={local.min_volume_24h}
              onChange={e => set('min_volume_24h', parseFloat(e.target.value) || 0)}
              className={`w-full text-xs px-3 py-2 rounded-lg border outline-none ${t.inp}`} />
          </div>

        </div>
        <div className={`flex gap-2 px-6 py-4 border-t ${t.cardBorder}`}>
          <button onClick={() => onSave(local)}
            className="flex-1 py-2 rounded-xl text-sm font-semibold bg-[#F0B90B] hover:bg-[#d4a209] text-black transition-colors">Kaydet</button>
          <button onClick={onClose}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold ${t.btnSecondary} transition-colors`}>İptal</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface Props { dark?: boolean }
type SortKey = 'base' | 'est_funding' | 'basis' | 'volume' | 'next_funding';
type SortDir = 'asc' | 'desc';

const BINANCE_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8881`
  : 'wss://ws.barbarosquant.org/binance-ws';

export default function BinanceArb({ dark = true }: Props) {
  const t = getTheme(dark);
  const { showToast } = useToast();

  const [tickers,    setTickers]    = useState<BinanceTicker[]>([]);
  const [account,    setAccount]    = useState<BinanceAccount | null>(null);
  const [arbs,       setArbs]       = useState<ArbPos[]>([]);
  const [cfg,        setCfg]        = useState<BinanceConfig>({
    active_pairs: [], funding_threshold: 0.0001, position_size_usdc: 100,
    leverage: 10, max_open_positions: 0, min_volume_24h: 0,  // 0 = tümünü göster
  });
  const [showCfg,    setShowCfg]    = useState(false);
  const [entryTicker, setEntryTicker] = useState<BinanceTicker | null>(null);
  const [search,     setSearch]     = useState('');
  const [sortKey,    setSortKey]    = useState<SortKey>('est_funding');
  const [sortDir,    setSortDir]    = useState<SortDir>('desc');
  const [favs,       setFavs]       = useState<string[]>(loadFavs);
  const [showFavs,   setShowFavs]   = useState(false);
  const [lastUpdate, setLastUpdate] = useState('—');
  const [connected,  setConnected]  = useState(false);

  const wsRef = useRef<WebSocket | null>(null);

  const send = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN)
      wsRef.current.send(JSON.stringify(msg));
  }, []);

  useEffect(() => {
    let dead = false;
    let retryTimer: ReturnType<typeof setTimeout>;
    function connect() {
      if (dead) return;
      const ws = new WebSocket(BINANCE_WS);
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); ws.send(JSON.stringify({ type: 'binance_get_state' })); };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'binance_tickers' && Array.isArray(msg.payload)) {
            setTickers(msg.payload);
            setLastUpdate(new Date().toLocaleTimeString('tr-TR'));
          } else if (msg.type === 'binance_account') {
            setAccount(msg.payload);
          } else if (msg.type === 'binance_arbs' && Array.isArray(msg.payload)) {
            setArbs(msg.payload);
          } else if (msg.type === 'binance_config') {
            setCfg(msg.payload);
          } else if (msg.type === 'binance_arb_result') {
            const p = msg.payload;
            if (p && !p.success) {
              const errMsg = p.error || p.msg || 'Emir gönderilemedi';
              showToast('Emir Reddedildi', errMsg, 'error');
            } else if (p && p.success) {
              const pair = p.data ? `${p.data.base}/${p.data.quote ?? 'USDC'}` : '';
              showToast('Pozisyon Açıldı', `${pair} arb pozisyonu açılıyor`, 'success');
            }
          }
        } catch {}
      };
      ws.onclose = () => { setConnected(false); if (!dead) retryTimer = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    }
    connect();
    return () => { dead = true; clearTimeout(retryTimer); wsRef.current?.close(); };
  }, []);

  const saveCfg = (c: BinanceConfig) => { send({ type: 'binance_update_config', payload: c }); setCfg(c); setShowCfg(false); };
  const toggleFav = (sym: string) => {
    const next = favs.includes(sym) ? favs.filter(f => f !== sym) : [...favs, sym];
    setFavs(next); localStorage.setItem(FAVS_KEY, JSON.stringify(next));
  };
  const handleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const filtered = tickers.filter(tk => {
    if (search && !tk.base.toLowerCase().includes(search.toLowerCase())) return false;
    if (showFavs && !favs.includes(tk.spot_sym)) return false;
    if (cfg.min_volume_24h > 0 && tk.volume_24h < cfg.min_volume_24h) return false;
    return true;
  }).sort((a, b) => {
    let va = 0, vb = 0;
    if (sortKey === 'base')         { va = a.base < b.base ? -1 : 1; return sortDir === 'asc' ? va : -va; }
    if (sortKey === 'est_funding')  { va = Math.abs(a.est_funding); vb = Math.abs(b.est_funding); }
    if (sortKey === 'basis')        { va = Math.abs(a.basis_pct);   vb = Math.abs(b.basis_pct); }
    if (sortKey === 'volume')       { va = a.volume_24h; vb = b.volume_24h; }
    if (sortKey === 'next_funding') { va = a.next_funding_in; vb = b.next_funding_in; }
    return sortDir === 'desc' ? vb - va : va - vb;
  });

  const openArbs   = arbs.filter(a => ['open','opening','partial_close'].includes(a.status));
  const closedArbs = arbs.filter(a => ['closed','aborted'].includes(a.status));

  const SortIcon = ({ k }: { k: SortKey }) => (
    <span className={`ml-0.5 ${sortKey === k ? 'text-[#F0B90B]' : dark ? 'text-blue-700' : 'text-blue-300'}`}>
      {sortKey === k ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}
    </span>
  );
  const thCls = `px-3 py-2 text-left text-xs font-semibold ${dark ? 'text-blue-400' : 'text-blue-600'} whitespace-nowrap cursor-pointer select-none`;

  return (
    <div className={`p-4 space-y-4 min-h-screen ${t.bg}`}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black tracking-wide text-[#F0B90B]">Binance Funding Arb</h1>
          <p className={`text-xs mt-0.5 ${dark ? 'text-blue-500' : 'text-blue-400'}`}>
            USDC &amp; USDT Spot + Perpetual Futures · {filtered.length}/{tickers.length} çift · Son güncelleme: {lastUpdate}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg ${dark ? 'bg-[#061530]' : 'bg-blue-100'}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-rose-500'}`} />
            <span className={dark ? 'text-blue-400' : 'text-blue-600'}>{connected ? 'Bağlı' : 'Bağlanıyor...'}</span>
          </div>
          <button onClick={() => setShowCfg(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold ${t.btnSecondary} transition-colors`}>
            <Settings size={13} /> Ayarlar
          </button>
        </div>
      </div>

      {/* Account Summary */}
      {account && (
        <div className="grid grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Spot USDC',       value: `$${(account.spot_usdc          ?? 0).toFixed(2)}` },
            { label: 'Spot USDT',       value: `$${(account.spot_usdt          ?? 0).toFixed(2)}` },
            { label: 'Fut USDC',        value: `$${(account.fut_usdc_balance   ?? 0).toFixed(2)}` },
            { label: 'Fut USDT',        value: `$${(account.fut_usdt_balance   ?? 0).toFixed(2)}` },
            { label: 'Müsait USDC',     value: `$${(account.fut_usdc_available ?? 0).toFixed(2)}` },
            { label: 'Müsait USDT',     value: `$${(account.fut_usdt_available ?? 0).toFixed(2)}` },
            { label: 'Gerçekleşmemiş PnL', value: `$${(account.fut_unrealized ?? 0).toFixed(2)}`,
              color: (account.fut_unrealized ?? 0) >= 0 ? t.positive : t.negative },
          ].map((item, i) => (
            <div key={i} className={`${t.card} border ${t.cardBorder} rounded-xl px-4 py-3`}>
              <div className={`text-xs ${dark ? 'text-blue-500' : 'text-blue-400'} mb-1`}>{item.label}</div>
              <div className={`text-sm font-bold ${item.color || ''}`}>{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Open Arb Positions */}
      {openArbs.length > 0 && (
        <div className="space-y-2">
          <h2 className={`text-sm font-bold ${t.text}`}>Aktif Pozisyonlar ({openArbs.length})</h2>
          {openArbs.map(arb => (
            <ArbCard key={arb.id} arb={arb} ticker={tickers.find(tk => tk.spot_sym === arb.spot_sym)} dark={dark}
              onClose={(id, pct) => send({ type: 'binance_close_arb', payload: { id, pct } })}
              onAbort={(id) => send({ type: 'binance_abort_arb', payload: { id } })}
              onDelete={(id) => send({ type: 'binance_delete_arb', payload: { id } })}
              onPartialClose={(id, pct) => send({ type: 'binance_close_arb', payload: { id, pct } })} />
          ))}
        </div>
      )}

      {/* Market table */}
      <div className={`${t.card} border ${t.cardBorder} rounded-2xl overflow-hidden`}>
        {/* Toolbar */}
        <div className={`flex items-center gap-3 px-4 py-3 border-b ${t.cardBorder}`}>
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${dark ? 'text-blue-600' : 'text-blue-400'}`} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Coin ara..."
              className={`w-full text-xs pl-8 pr-3 py-1.5 rounded-lg border outline-none ${t.inp}`} />
          </div>
          <button onClick={() => setShowFavs(!showFavs)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-colors ${showFavs ? 'bg-[#F0B90B]/20 text-[#F0B90B]' : t.btnSecondary}`}>
            <Star size={12} /> Favoriler
          </button>
          <span className={`text-xs ${dark ? 'text-blue-600' : 'text-blue-400'}`}>{filtered.length} çift</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${t.cardBorder} ${dark ? 'bg-[#030d1f]/50' : 'bg-blue-50'}`}>
                <th className={thCls + ' w-8'}></th>
                <th className={thCls} onClick={() => handleSort('base')}>Çift <SortIcon k="base" /></th>
                <th className={`${thCls} text-emerald-400`}>Spot Bid</th>
                <th className={`${thCls} text-rose-400`}>Spot Ask</th>
                <th className={`${thCls} text-emerald-400`}>Fut Bid</th>
                <th className={`${thCls} text-rose-400`}>Fut Ask</th>
                <th className={thCls}>Mark</th>
                <th className={thCls} onClick={() => handleSort('basis')}>Basis <SortIcon k="basis" /></th>
                <th className={thCls} onClick={() => handleSort('est_funding')}>Tahmini <SortIcon k="est_funding" /></th>
                <th className={thCls}>Son Funding</th>
                <th className={thCls} onClick={() => handleSort('next_funding')}>Sonraki <SortIcon k="next_funding" /></th>
                <th className={thCls}>Spot Fee</th>
                <th className={thCls}>Fut Fee</th>
                <th className={thCls} onClick={() => handleSort('volume')}>Hacim 24s <SortIcon k="volume" /></th>
                <th className={thCls}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(tk => {
                const isOpen = arbs.some(a => a.spot_sym === tk.spot_sym && ['open','opening'].includes(a.status));
                const meetsThreshold = Math.abs(tk.est_funding) >= cfg.funding_threshold;
                return (
                  <tr key={tk.spot_sym} className={`border-b ${t.tableRow} text-xs`}>
                    <td className="px-2 py-2.5 text-center">
                      <button onClick={() => toggleFav(tk.spot_sym)}>
                        <Star size={12} className={favs.includes(tk.spot_sym) ? 'text-[#F0B90B] fill-[#F0B90B]' : (dark ? 'text-blue-700' : 'text-blue-300')} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold">
                        {tk.base}
                        <span className={`text-xs ${tk.quote === 'USDT' ? 'text-emerald-500' : 'text-amber-500'}`}>/{tk.quote}</span>
                      </div>
                    </td>
                    <td className={`px-3 py-2.5 font-mono ${t.positive}`}>{fmtPrice(tk.spot_bid)}</td>
                    <td className={`px-3 py-2.5 font-mono ${t.negative}`}>{fmtPrice(tk.spot_ask)}</td>
                    <td className={`px-3 py-2.5 font-mono ${t.positive}`}>{fmtPrice(tk.fut_bid)}</td>
                    <td className={`px-3 py-2.5 font-mono ${t.negative}`}>{fmtPrice(tk.fut_ask)}</td>
                    <td className="px-3 py-2.5 font-mono">{fmtPrice(tk.mark_price)}</td>
                    <td className={`px-3 py-2.5 font-mono ${(tk.basis_pct ?? 0) >= 0 ? t.positive : t.negative}`}>
                      {(tk.basis_pct ?? 0) >= 0 ? '+' : ''}{(tk.basis_pct ?? 0).toFixed(4)}%
                    </td>
                    <td className={`px-3 py-2.5 font-mono font-bold ${(tk.predicted_funding ?? 0) >= 0 ? t.positive : t.negative}`}>
                      {meetsThreshold && <span className="mr-1">⚡</span>}
                      {(tk.predicted_funding ?? 0) >= 0 ? '+' : ''}{fmtPct(tk.predicted_funding ?? 0)}
                    </td>
                    <td className={`px-3 py-2.5 font-mono text-xs ${(tk.last_funding ?? 0) >= 0 ? t.positive : t.negative} opacity-60`}>
                      {(tk.last_funding ?? 0) >= 0 ? '+' : ''}{fmtPct(tk.last_funding ?? 0)}
                    </td>
                    <td className="px-3 py-2.5 font-mono">
                      <span className={dark ? 'text-blue-300' : 'text-blue-700'}>{fmtCountdown(tk.next_funding_in)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      <div className={dark ? 'text-blue-400' : 'text-blue-500'}>M {((tk.spot_maker_fee ?? 0) * 100).toFixed(3)}%</div>
                      <div className={dark ? 'text-blue-600' : 'text-blue-400'}>T {((tk.spot_taker_fee ?? 0) * 100).toFixed(3)}%</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      <div className={dark ? 'text-blue-400' : 'text-blue-500'}>M {((tk.fut_maker_fee ?? 0) * 100).toFixed(3)}%</div>
                      <div className={dark ? 'text-blue-600' : 'text-blue-400'}>T {((tk.fut_taker_fee ?? 0) * 100).toFixed(3)}%</div>
                    </td>
                    <td className="px-3 py-2.5">${fmtVol(tk.volume_24h)}</td>
                    <td className="px-3 py-2.5">
                      {isOpen ? (
                        <span className={`text-xs font-semibold ${t.positive}`}>● Açık</span>
                      ) : (
                        <button
                          onClick={() => setEntryTicker(tk)}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                            meetsThreshold
                              ? 'bg-[#F0B90B] hover:bg-[#d4a209] text-black'
                              : dark
                                ? 'bg-[#0a2560]/60 text-blue-400 hover:bg-[#0d3080] hover:text-white border border-[#003087]'
                                : 'bg-blue-100 text-blue-500 hover:bg-blue-200 border border-blue-300'
                          }`}
                        >
                          <Zap size={10} /> Aç
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className={`text-center py-12 ${dark ? 'text-blue-600' : 'text-blue-300'}`}>
              {tickers.length === 0 ? 'Veri bekleniyor...' : 'Eşleşen çift yok'}
            </div>
          )}
        </div>
      </div>

      {/* Closed arbs */}
      {closedArbs.length > 0 && (
        <div className="space-y-2">
          <h2 className={`text-sm font-bold ${dark ? 'text-blue-500' : 'text-blue-400'}`}>Kapanan ({closedArbs.length})</h2>
          {closedArbs.slice(0, 10).map(arb => (
            <ArbCard key={arb.id} arb={arb} ticker={tickers.find(tk => tk.spot_sym === arb.spot_sym)} dark={dark}
              onClose={() => {}} onAbort={() => {}}
              onDelete={(id) => send({ type: 'binance_delete_arb', payload: { id } })}
              onPartialClose={() => {}} />
          ))}
        </div>
      )}

      {/* Entry Modal */}
      {entryTicker && (
        <EntryModal
          ticker={entryTicker}
          cfg={cfg}
          dark={dark}
          onConfirm={(payload) => {
            send({ type: 'binance_enter_arb', payload });
            setEntryTicker(null);
          }}
          onClose={() => setEntryTicker(null)}
        />
      )}

      {/* Config Modal */}
      {showCfg && (
        <ConfigModal cfg={cfg} tickers={tickers} dark={dark} onSave={saveCfg} onClose={() => setShowCfg(false)} />
      )}
    </div>
  );
}
